import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { acquireLock, heartbeatLock, releaseLock } from '@/lib/locks/api';
import { LockConflictError, type LockState } from '@/lib/locks/types';
import { useLockState } from './use-lock-state';

// Lock lifecycle hook per docs/ui/operator-ui-tds.md §5.2:
//   - Acquire on explicit operator action (never on mount)
//   - Heartbeat every 10s while held + document visible
//   - Pause heartbeat on visibilitychange to hidden; BFF TTL takes over
//   - Release on: explicit click, navigation away (component unmount),
//     document hidden > 60s
//   - 409 from BFF acquire never silently retries

const HEARTBEAT_INTERVAL_MS = 10_000;
const HIDDEN_RELEASE_AFTER_MS = 60_000;

export interface UseLockLifecycleResult {
  acquire: () => Promise<void>;
  release: () => Promise<void>;
  isAcquiring: boolean;
  isReleasing: boolean;
  acquireError: Error | null;
  conflict: LockConflictError | null;
}

export function useLockLifecycle(entityUrn: string): UseLockLifecycleResult {
  const qc = useQueryClient();
  const { isMine } = useLockState(entityUrn);
  const heartbeatTimerRef = useRef<number | null>(null);
  const hiddenTimerRef = useRef<number | null>(null);
  const conflictRef = useRef<LockConflictError | null>(null);

  const writeLockCache = useCallback(
    (next: LockState) => {
      qc.setQueryData(['cases', entityUrn, 'lock'], next);
    },
    [qc, entityUrn],
  );

  const acquireMutation = useMutation({
    mutationFn: () => acquireLock(entityUrn),
    onSuccess: (next) => {
      conflictRef.current = null;
      writeLockCache(next);
    },
    onError: (err) => {
      if (err instanceof LockConflictError) {
        conflictRef.current = err;
        writeLockCache({
          entityUrn,
          holder: err.holder,
          expiresAt: err.expiresAt,
        });
      }
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () => releaseLock(entityUrn),
    onSuccess: () => {
      writeLockCache({ entityUrn, holder: null, expiresAt: null });
    },
  });

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current !== null) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatTimerRef.current = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      heartbeatLock(entityUrn)
        .then((next) => {
          if (next) writeLockCache(next);
        })
        .catch(() => {
          // Heartbeat failure is non-fatal; the next interval or a manual
          // refetch surfaces the conflict.
        });
    }, HEARTBEAT_INTERVAL_MS);
  }, [entityUrn, stopHeartbeat, writeLockCache]);

  // Manage heartbeat lifecycle keyed on isMine.
  useEffect(() => {
    if (!isMine) {
      stopHeartbeat();
      return;
    }
    if (document.visibilityState === 'visible') {
      startHeartbeat();
    }
    return () => stopHeartbeat();
  }, [isMine, startHeartbeat, stopHeartbeat]);

  // Visibility-driven heartbeat pause + 60s hidden release.
  useEffect(() => {
    if (!isMine) return;

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        if (hiddenTimerRef.current !== null) {
          window.clearTimeout(hiddenTimerRef.current);
          hiddenTimerRef.current = null;
        }
        startHeartbeat();
      } else {
        stopHeartbeat();
        if (hiddenTimerRef.current !== null) window.clearTimeout(hiddenTimerRef.current);
        hiddenTimerRef.current = window.setTimeout(() => {
          void releaseMutation.mutateAsync();
        }, HIDDEN_RELEASE_AFTER_MS);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (hiddenTimerRef.current !== null) {
        window.clearTimeout(hiddenTimerRef.current);
        hiddenTimerRef.current = null;
      }
    };
  }, [isMine, releaseMutation, startHeartbeat, stopHeartbeat]);

  // Release on unmount if we held the lock (navigation away per §5.2).
  useEffect(() => {
    return () => {
      if (isMine) {
        void releaseLock(entityUrn).catch(() => {
          // Best-effort release — OperatorDisconnected (NEX-71) is the safety net.
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acquire = useCallback(async () => {
    await acquireMutation.mutateAsync();
  }, [acquireMutation]);

  const release = useCallback(async () => {
    await releaseMutation.mutateAsync();
  }, [releaseMutation]);

  return {
    acquire,
    release,
    isAcquiring: acquireMutation.isPending,
    isReleasing: releaseMutation.isPending,
    acquireError: acquireMutation.error instanceof LockConflictError ? null : acquireMutation.error,
    conflict: acquireMutation.error instanceof LockConflictError ? acquireMutation.error : null,
  };
}
